import { createSlice } from '@reduxjs/toolkit';
import { pathOr } from 'ramda';
import moment from 'moment';
import { validateModel, validateFormField } from '../validation/employmentDetails';
import { canProceed } from '../utils/validation/canProceed';
import { personalDetails } from './personalDetails';
import { isEconomicallyInactive } from '../utils/employment';
import { SELF_EMPLOYED } from 'constants/general';

const initialState = {
  validation: {},
  hasTriedToSave: false,
  isDirty: false,
  isSaving: false,
  isLoading: false,
  canContinue: false,
  model: {
    employmentStatus: '',
    employerName: '',
    employmentStartDate: '',
    employmentJobLevel: '',
    employmentIndustry: '',
    grossAnnualIncome: '',
    netMonthlyIncome: '',
    financialCircumstances: '',
    mortgageExpense: '',
    earnIncome: ''
  },
  personalDetails: {
    birthDate: '',
    title: '',
    firstName: '',
    middleName: '',
    lastName: ''
  }
};

const employmentDetails = createSlice({
  name: 'employmentDetails',
  initialState,

  reducers: {
    loadEmploymentDetails: (state) => {
      state.isLoading = true;
    },
    loadEmploymentDetailsCompleted: (state, action) => {
      state.model = action.payload;
      state.isLoading = false;
    },
    loadEmploymentDetailsRejected: (state) => {
      state.isLoading = false;
    },
    modelChange: (state, action) => {
      state.model = action.payload;
      state.isDirty = true;
    },
    fieldChange: (state, { payload: { field, value, section } }) => {
      if (section) {
        state.model[section][field] = value;
      } else {
        state.model[field] = value;
      }
      //reset the other value empty if status is economicaly in active
      if (field === 'employmentStatus' && isEconomicallyInactive(value)) {
        state.model.employerName = '';
        state.model.employmentStartDate = '';
        state.model.employmentIndustry = '';
        state.model.employmentJobLevel = '';
      } else if (field === 'employmentStatus' && value === SELF_EMPLOYED) {
        state.model.employmentJobLevel = '';
      }
      state.isDirty = true;
    },
    validate: (state, action) => {
      state.validation = validateModel(action.payload, state.personalDetails);
      state.canContinue = canProceed(state.validation);
    },
    validateForReview: (state, action) => {
      state.validation = validateModel(action.payload, state.personalDetails);
    },
    validateField: (state, action) => {
      const { field, value } = action.payload;

      state.validation[field] = validateFormField(field, value);
      state.canContinue = canProceed(state.validation);
    },
    resetValidation: (state) => {
      state.validation = initialState.validation;
      state.canContinue = false;
    },
    save: (state) => {
      state.isSaving = true;
      state.hasTriedToSave = true;
    },
    saveCompleted: (state) => {
      state.isSaving = false;
    },
    saveCancelled: (state) => {
      state.isSaving = false;
    },
    saveRejected: (state) => {
      state.isSaving = false;
    },
    resetState: () => initialState,
    updateEmploymentDetails: (state, action) => {
      const { employment_details, personal_details } = action.payload;
      const [employmentDetails] = employment_details;

      state.model = {
        ...state.model,
        ...(employmentDetails && {
          employmentStatus: employmentDetails.status || state.model.employmentStatus,
          employerName:
            (employmentDetails.employer && employmentDetails.employer.name) ||
            state.model.employerName,
          employmentStartDate: pathOr(
            state.model.employmentStartDate,
            ['effective_period', 'from'],
            employmentDetails
          )
        })
      };

      const {
        name: { title, first_name, middle_name, last_name },
        dob
      } = personal_details;

      state.personalDetails = {
        birthDate: dob && moment(dob, 'YYYYMMDD', true).format('YYYY-MM-DD'),
        title: title || '',
        firstName: first_name,
        middleName: middle_name || '',
        lastName: last_name
      };
    }
  },

  extraReducers: (builder) => {
    builder.addCase(personalDetails.actions.fieldChange, (state, action) => {
      const { field } = action.payload;

      switch (field) {
        case 'birthDate':
        case 'firstName':
        case 'middleName':
        case 'lastName':
        case 'title':
          state.personalDetails[field] = action.payload.value;
          break;
        default:
          break;
      }
    });

    builder.addCase(personalDetails.actions.modelChange, (state, action) => {
      Object.entries(action.payload).forEach(([key, value]) => {
        switch (key) {
          case 'birthDate':
          case 'firstName':
          case 'middleName':
          case 'lastName':
          case 'title':
            state.personalDetails[key] = value;
            break;
          default:
            break;
        }
      });
    });
  }
});

export function tryToContinue(model, callback) {
  return (dispatch) => {
    dispatch(validate(model));
    dispatch(checkCanContinue(callback));
  };
}

function checkCanContinue(callback) {
  return (dispatch, getState) => {
    const { canContinue } = getState().employmentDetails;
    if (canContinue) {
      if (typeof callback === 'function') {
        callback();
      }
      dispatch(resetValidation());
    }
  };
}

export function confirmChanges(editState, callback) {
  return (dispatch) => {
    dispatch(validate(editState));
    dispatch(confirmModelChanges(editState, callback));
  };
}

function confirmModelChanges(editState, callback) {
  return (dispatch, getState) => {
    const { canContinue } = getState().employmentDetails;
    if (canContinue) {
      dispatch(modelChange(editState));
      if (typeof callback === 'function') {
        callback();
      }
    }
  };
}

export { employmentDetails };
export const {
  fieldChange,
  loadEmploymentDetails,
  updateEmploymentDetails,
  loadEmploymentDetailsCompleted,
  loadEmploymentDetailsRejected,
  modelChange,
  resetValidation,
  save,
  saveCancelled,
  saveCompleted,
  saveRejected,
  validate,
  validateForReview,
  validateField,
  resetState
} = employmentDetails.actions;
